import React, { useMemo } from "react";
import FormDivider from "@components/FormDivider/FormDivider.tsx";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { FormContext } from "@features/port-security/components/SecurityClearence.types.ts";
import { buildCountryOptions } from "@features/port-security/components/utils";
import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import type { Nationality } from "@services/gatepass/types";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { stringToTitleCase } from "@utils/formatters/strings";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

const FirstGradeRelations: React.FC = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const { options: nationalities } = useFormattedReference<Nationality>(
    REFERENCE_DATA_TYPES.NATIONALITY,
    "bff-gatepass",
  );
  const { options: countries } = useFormattedReference<{
    countryName: string;
    countryCode: string;
  }>(REFERENCE_DATA_TYPES.COUNTRIES, "bff-gatepass");
  const nationalitiesOptions = useMemo(
    () =>
      nationalities
        .map((n) => ({ label: n.nationality, value: n.nationality }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [nationalities],
  );
  const countriesOptions = useMemo(
    () => buildCountryOptions(countries, { valueMode: "code", formatName: stringToTitleCase }),
    [countries],
  );

  return (
    <FormProvider {...formData}>
      <FormSection title={t("security-clearance.blood-relations-1st")}>
        <FormItem<PortSecurityFormData, "fatherName">
          name="fatherName"
          type="text"
          maxChars={200}
          label={t("security-clearance.father-name")}
          testId="security-fatherName"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "fatherNationality">
          name="fatherNationality"
          type="autocomplete"
          options={nationalitiesOptions}
          label={t("security-clearance.nationality")}
          testId="security-fatherNationality"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "fatherBirthPlace">
          name="fatherBirthPlace"
          type="autocomplete"
          options={countriesOptions}
          label={t("security-clearance.birth-place")}
          testId="security-fatherBirthPlace"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "fatherBirthDate">
          name="fatherBirthDate"
          type="date-picker"
          label={t("security-clearance.birth-date")}
          testId="security-fatherBirthDate"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="past"
        />
        <FormItem<PortSecurityFormData, "fatherWorkPlace">
          name="fatherWorkPlace"
          type="text"
          maxChars={200}
          label={t("security-clearance.work-place")}
          testId="security-fatherWorkPlace"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "fatherOccupation">
          name="fatherOccupation"
          type="text"
          maxChars={200}
          label={t("security-clearance.occupation")}
          testId="security-fatherOccupation"
          onFieldChange={updateFormData}
        />
        <div className="col-span-full">
          <FormDivider />
        </div>
        <FormItem<PortSecurityFormData, "motherName">
          name="motherName"
          type="text"
          maxChars={200}
          label={t("security-clearance.mother-name")}
          testId="security-motherName"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "motherNationality">
          name="motherNationality"
          type="autocomplete"
          options={nationalitiesOptions}
          label={t("security-clearance.nationality")}
          testId="security-motherNationality"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "motherBirthPlace">
          name="motherBirthPlace"
          type="autocomplete"
          options={countriesOptions}
          label={t("security-clearance.birth-place")}
          testId="security-motherBirthPlace"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "motherBirthDate">
          name="motherBirthDate"
          type="date-picker"
          label={t("security-clearance.birth-date")}
          testId="security-motherBirthDate"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="past"
        />
        <FormItem<PortSecurityFormData, "motherWorkPlace">
          name="motherWorkPlace"
          type="text"
          maxChars={200}
          label={t("security-clearance.work-place")}
          testId="security-motherWorkPlace"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "motherOccupation">
          name="motherOccupation"
          type="text"
          maxChars={200}
          label={t("security-clearance.occupation")}
          testId="security-motherOccupation"
          onFieldChange={updateFormData}
        />
      </FormSection>
    </FormProvider>
  );
};

export default FirstGradeRelations;
